import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Check, Moon } from "lucide-react";
import { seo } from "@/lib/seo";
import { PALETTES, useTheme } from "@/hooks/use-theme";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/appearance")({
  head: () =>
    seo({
      title: "Appearance — The Quantum Minds",
      description: "Choose how The Quantum Minds looks across the app.",
      noindex: true,
    }),
  component: AppearancePage,
});

function AppearancePage() {
  const { palette, setPalette, theme, toggle, useSystem, setUseSystem } = useTheme();

  return (
    <div className="mx-auto w-full max-w-2xl px-5 pb-32 pt-8">
      <div className="flex items-center gap-3">
        <Link
          to="/profile"
          aria-label="Back to profile"
          className="glass grid size-10 place-items-center rounded-full"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Appearance</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Choose how The Quantum Minds looks across the app.
          </p>
        </div>
      </div>

      <h2 className="mt-8 px-1 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
        Theme
      </h2>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {PALETTES.map((p) => {
          const active = palette === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setPalette(p.id)}
              aria-pressed={active}
              className={cn(
                "glass flex items-center gap-4 rounded-3xl p-4 text-left transition",
                active
                  ? "border-primary/50 shadow-[var(--shadow-glow)]"
                  : "hover:border-border-strong",
              )}
            >
              <span
                aria-hidden
                className="grid size-12 shrink-0 place-items-center rounded-2xl border border-border"
                style={{ backgroundColor: p.swatch[0] }}
              >
                <span
                  className="grid size-7 place-items-center rounded-xl"
                  style={{ backgroundColor: p.swatch[1] }}
                >
                  <span
                    className="size-3 rounded-full"
                    style={{ backgroundColor: p.swatch[2] }}
                  />
                </span>
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold">{p.name}</span>
                <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">
                  {p.description}
                </span>
              </span>
              {active ? (
                <span className="grid size-6 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground">
                  <Check className="size-3.5" />
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      <h2 className="mt-8 px-1 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
        Mode
      </h2>
      <div className="glass mt-3 overflow-hidden rounded-3xl">
        <Row
          title="Dark mode"
          subtitle="Use darker colours across the app"
          checked={theme === "dark"}
          disabled={useSystem}
          onChange={toggle}
          icon={<Moon className="size-4 text-primary" />}
        />
        <div className="h-px bg-hairline" />
        <Row
          title="Use device appearance"
          subtitle="Follow your device's light or dark setting"
          checked={useSystem}
          onChange={() => setUseSystem(!useSystem)}
        />
      </div>
    </div>
  );
}

function Row({
  title,
  subtitle,
  checked,
  disabled,
  onChange,
  icon,
}: {
  title: string;
  subtitle: string;
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
  icon?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={onChange}
      className={cn(
        "flex w-full items-center gap-4 px-5 py-4 text-left transition",
        disabled ? "opacity-50" : "hover:bg-surface",
      )}
    >
      {icon ? (
        <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-surface">
          {icon}
        </span>
      ) : null}
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">{title}</span>
        <span className="mt-0.5 block text-xs text-muted-foreground">{subtitle}</span>
      </span>
      <span
        className={cn(
          "relative h-6 w-11 shrink-0 rounded-full transition-colors",
          checked ? "bg-primary" : "bg-surface border border-border",
        )}
      >
        <span
          className={cn(
            "absolute top-1 size-4 rounded-full bg-background transition-all",
            checked ? "left-6" : "left-1",
          )}
        />
      </span>
    </button>
  );
}
